import { useEffect, useRef } from 'react'

/**
 * Hides OneTrust's floating manager button while a dialog/modal is open.
 *
 * @param {boolean} isDialogOpen Whether the owning dialog/modal is currently open.
 * @param {object} options
 * @param {string} options.selector DOM selector for the OneTrust floating element.
 * @param {string} options.restoreDisplay Display value to use when the element had no prior inline display.
 */
const useOneTrustFloatingButtonVisibility = (
  isDialogOpen,
  { selector = '#ot-sdk-btn-floating', restoreDisplay = 'block' } = {},
) => {
  const hiddenElementsRef = useRef(new Map())

  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    const restoreHiddenElements = () => {
      const hiddenElements = Array.from(hiddenElementsRef.current.entries())

      for (let index = 0; index < hiddenElements.length; index += 1) {
        const [element, priorDisplay] = hiddenElements[index]
        element.style.display = priorDisplay || restoreDisplay
      }

      hiddenElementsRef.current.clear()
    }

    const hideMatchingElements = () => {
      const elements = Array.from(document.querySelectorAll(selector))

      for (let index = 0; index < elements.length; index += 1) {
        const element = elements[index]

        if (!hiddenElementsRef.current.has(element)) {
          hiddenElementsRef.current.set(element, element.style.display)
        }

        element.style.display = 'none'
      }
    }

    if (!isDialogOpen) {
      restoreHiddenElements()
      return undefined
    }

    hideMatchingElements()

    const observer = typeof MutationObserver === 'function'
      ? new MutationObserver(hideMatchingElements)
      : null

    if (observer && document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      if (observer) {
        observer.disconnect()
      }

      // If the owning component unmounts while the modal is still open (route transition, filtering, etc),
      // restore the previously stored inline display so OneTrust doesn't stay hidden.
      restoreHiddenElements()
    }
  }, [isDialogOpen, restoreDisplay, selector])
}

export default useOneTrustFloatingButtonVisibility
